import type { ReactElement, ReactNode } from "react"
import React, { useEffect } from "react"
import type { IconType } from "react-icons"
import { BiLogOut, BiMenu } from "react-icons/bi"
import { BsArrowBarLeft } from "react-icons/bs"
import { PiBookOpen, PiCaretDownLight, PiGear, PiHouse } from "react-icons/pi"
import { RiAdminLine, RiPagesLine } from "react-icons/ri"
import { TbAdjustments, TbTruckLoading, TbUsers } from "react-icons/tb"
import NextLink from "next/link"
import { useRouter } from "next/router"

import type { FlexProps, LinkProps } from "@chakra-ui/react"
import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  Circle,
  Flex,
  forwardRef,
  HStack,
  Icon,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Stack,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react"

import { useFlag } from "@reflag/react-sdk"
import { useQuery } from "@tanstack/react-query"

import { useSessionStorage } from "@supliful/common"

import { Logo } from "components/Logo"
import { AdminPermission } from "lib/graphql"
import { useLogout } from "lib/hooks/useLogout"
import { useMe } from "lib/hooks/useMe"
import { useWarehouses } from "lib/hooks/useWarehouses"
import { isActive, routes } from "lib/routes"
import { useTRPC } from "lib/trpc"
import { isAuthorized } from "lib/utils/common.utils"

const SIDEBAR_PADDING = { base: 4 }

export function Sidebar(flexProps: FlexProps) {
  const { me } = useMe()
  const [isCollapsed, setIsCollapsed] = useSessionStorage(
    "adminSidebarIsCollapsed",
    false,
  )
  const router = useRouter()
  const logout = useLogout()
  const bgColor = useColorModeValue("neutrals.20", "neutrals.900")
  const borderColor = useColorModeValue("neutrals.200", "neutrals.900")

  const { sidebarItemConfig } = useSidebarItemConfig()

  return (
    <Flex
      id="admin-sidebar"
      data-testid="sidebar"
      position="relative"
      h="100dvh"
      direction="column"
      flexShrink={0}
      pt={8}
      bg={bgColor}
      borderRight="1px solid"
      borderColor={borderColor}
      transition="width 0.3s ease-in-out"
      {...flexProps}
      {...(isCollapsed && { w: "3.5rem" })}
    >
      <IconButton
        aria-label={isCollapsed ? "Expand menu" : "Collapse menu"}
        icon={
          isCollapsed ? (
            <Icon as={BiMenu} fontSize={18} />
          ) : (
            <Icon as={BsArrowBarLeft} fontSize={18} />
          )
        }
        colorScheme="tertiary"
        size="md"
        onClick={() => setIsCollapsed(!isCollapsed)}
        position="absolute"
        top={1}
        right={-5}
        mb={8}
        zIndex={4}
      />

      <Link as={NextLink} href="/" mb={6} px={SIDEBAR_PADDING}>
        <Logo narrow={isCollapsed} style={{ flexShrink: 0 }} />
      </Link>

      <Box
        w="full"
        flexGrow={2}
        overflow="hidden"
        overflowY="auto"
        pl={2}
        py={1}
      >
        <Stack w={isCollapsed ? "2rem" : "14.5rem"} align="stretch" spacing={1}>
          {sidebarItemConfig.map((sidebarItem) =>
            sidebarItem.children ? (
              <SidebarItemGroup
                key={sidebarItem.title}
                title={sidebarItem.title}
                icon={sidebarItem.icon}
                isCollapsed={isCollapsed}
                hasAlertIndicator={sidebarItem.children.some(
                  (c) => c.alertCount,
                )}
                hasActiveChild={sidebarItem.children.some(({ href }) =>
                  isActive(router.asPath, href),
                )}
                data-testid={`sidebar-group-${sidebarItem.testId}`}
              >
                {sidebarItem.children.map(
                  ({ href, title, testId, alertCount }) => (
                    <SidebarLink
                      href={href}
                      key={title}
                      data-testid={`sidebar-link-${testId}`}
                    >
                      <HStack justify="space-between">
                        <Text fontSize={14}>{title}</Text>

                        {!!alertCount && (
                          <Badge
                            colorScheme="red"
                            variant="solid"
                            lineHeight={1}
                            fontSize="xs"
                            alignContent="center"
                            px="1"
                            minW={5}
                            height={5}
                            textDecoration="none"
                          >
                            {alertCount}
                          </Badge>
                        )}
                      </HStack>
                    </SidebarLink>
                  ),
                )}
              </SidebarItemGroup>
            ) : (
              <SidebarItem
                key={sidebarItem.title}
                icon={<SidebarIcon icon={sidebarItem.icon} />}
                href={sidebarItem.href}
                isCollapsed={isCollapsed}
                data-testid={`sidebar-item-${sidebarItem.testId}`}
              >
                {sidebarItem.title}
              </SidebarItem>
            ),
          )}
        </Stack>
      </Box>

      <Stack
        px={isCollapsed ? 2 : SIDEBAR_PADDING}
        py={2}
        direction="row"
        justify="space-between"
        align="center"
        spacing={4}
        borderTop="1px solid"
        borderColor="border.primary"
      >
        {!isCollapsed && (
          <Stack direction="row" align="center">
            <Avatar size="sm" name={`${me?.firstName} ${me?.lastName}`} />
            <Text fontSize={12} noOfLines={1} maxW="8rem">
              {me?.email}
            </Text>
          </Stack>
        )}

        <Menu>
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<PiGear fontSize={20} />}
            variant="ghost"
            colorScheme="secondary"
          />

          <MenuList>
            {isAuthorized(me?.permissions, [
              AdminPermission.FulfillmentLead,
            ]) && (
              <MenuItem
                icon={<RiAdminLine />}
                onClick={() => router.push(routes.admins)}
              >
                Admins
              </MenuItem>
            )}

            <MenuItem icon={<BiLogOut />} onClick={() => logout()}>
              Logout
            </MenuItem>
          </MenuList>
        </Menu>
      </Stack>
    </Flex>
  )
}

interface SidebarItemGroupProps {
  title: string
  icon: IconType
  isCollapsed?: boolean
  children: ReactNode
  hasAlertIndicator?: boolean
  hasActiveChild?: boolean
  "data-testid": string
}

function SidebarItemGroup({
  title,
  icon,
  isCollapsed,
  children,
  hasAlertIndicator,
  hasActiveChild,
  "data-testid": testId,
}: SidebarItemGroupProps) {
  const { isOpen, onToggle, onOpen } = useDisclosure()

  useEffect(() => {
    if (hasActiveChild) {
      onOpen()
    }
  }, [hasActiveChild, onOpen])

  if (!children) {
    return null
  }

  return (
    <Stack direction="column" spacing={0} data-testid={testId}>
      <Popover>
        <PopoverTrigger>
          <SidebarItem
            isCollapsed={isCollapsed}
            icon={
              <SidebarIcon
                icon={icon}
                hasAlertIndicator={hasAlertIndicator}
                isActive={hasActiveChild}
                popoverTitle={title}
              />
            }
            onClick={onToggle}
            aria-label="Toggle submenu"
            data-testid={testId}
          >
            <Stack direction="row" justify="space-between" align="center">
              <Text
                color={hasActiveChild ? "text.action" : "black"}
                fontSize={15}
              >
                {title}
              </Text>

              {!isCollapsed && (
                <Icon
                  as={PiCaretDownLight}
                  transform={isOpen ? "rotate(-180deg)" : "rotate(0)"}
                  color="black"
                />
              )}
            </Stack>
          </SidebarItem>
        </PopoverTrigger>

        {isCollapsed ? (
          <Portal>
            <PopoverContent>
              <PopoverBody>{children}</PopoverBody>
            </PopoverContent>
          </Portal>
        ) : (
          <Stack
            display={isOpen ? "flex" : "none"}
            spacing={0}
            pl={3}
            borderLeft="1px solid"
            borderColor="border.primary"
            style={{ marginLeft: "calc(var(--chakra-space-4) + 1px)" }}
          >
            {children}
          </Stack>
        )}
      </Popover>
    </Stack>
  )
}

const SidebarIcon = ({
  icon,
  isActive,
  popoverTitle,
  hasAlertIndicator,
}: {
  icon: IconType
  isActive?: boolean
  popoverTitle?: string
  hasAlertIndicator?: boolean
}) => (
  <Tooltip label={popoverTitle ?? null}>
    <Flex lineHeight={1} position="relative">
      <Icon
        as={icon}
        w="1.25em"
        h="1.25em"
        color={isActive ? "text.action" : undefined}
      />
      {hasAlertIndicator && (
        <Center
          position="absolute"
          right={0}
          top={0}
          transform="translate(50%, -50%)"
        >
          <Circle bg="red" size="1.5" />
        </Center>
      )}
    </Flex>
  </Tooltip>
)

type SidebarItemProps = {
  icon?: ReactElement<typeof SidebarIcon>
  href?: string
  onClick?: () => void
  isCollapsed?: boolean
  children: ReactNode
  "data-testid"?: string
}

const SidebarItem = forwardRef<SidebarItemProps, "div">(
  (
    { icon, href, onClick, isCollapsed, children, "data-testid": dataTestId },
    ref,
  ) => {
    const ItemContent = () => (
      <Stack
        ref={ref}
        direction="row"
        align="center"
        spacing={3}
        w="full"
        minH={6}
      >
        {icon}

        {!isCollapsed && (
          <Box
            fontWeight={600}
            // fontSize={15}
            w="full"
          >
            {children}
          </Box>
        )}
      </Stack>
    )

    if (href) {
      return (
        <SidebarLink href={href} data-testid={dataTestId}>
          <ItemContent />
        </SidebarLink>
      )
    }

    return (
      <Button
        variant="unstyled"
        onClick={onClick}
        h="auto"
        minW={10}
        p={2}
        fontSize={15}
        borderRadius="md"
        _hover={{
          bg: "neutrals.100",
        }}
        data-testid={dataTestId}
      >
        <ItemContent />
      </Button>
    )
  },
)

interface SidebarLinkProps extends LinkProps {
  href: string
}

function SidebarLink({ href, children, ...props }: SidebarLinkProps) {
  const router = useRouter()

  const isActiveLink = isActive(router.asPath, href)

  return (
    <Link
      as={NextLink}
      href={href}
      passHref
      display="block"
      color={isActiveLink ? "text.action" : "black"}
      borderRadius="md"
      minW="min-content"
      fontSize={15}
      p={2}
      _hover={{
        bg: "neutrals.100",
      }}
      {...props}
    >
      {children}
    </Link>
  )
}

type BaseSidebarItem = {
  title: string
  icon: IconType
  testId: string
}

type SingleLevelSidebarItem = BaseSidebarItem & {
  href: string
  alertCount?: number
}

type ChildSidebarItem = {
  title: string
  href: string
  testId: string
  alertCount?: number
}

type SideBarItemConfigWithVisibility =
  | (BaseSidebarItem & {
      children: (ChildSidebarItem & {
        isVisible: boolean
      })[]
      href?: never
    })
  | (SingleLevelSidebarItem & {
      children?: never
      isVisible?: boolean
    })

type SidebarItemConfig =
  | (BaseSidebarItem & { children: ChildSidebarItem[]; href?: never })
  | (SingleLevelSidebarItem & { children?: never })
function useSidebarItemConfig() {
  const { me } = useMe()
  const { warehouses } = useWarehouses()

  const trpc = useTRPC()

  const refetchInterval = 1000 * 60 * 3 // Poll every 3 minutes

  const labelRollSidebarVisible = isAuthorized(me.permissions, [
    AdminPermission.PartnerProgramFeatures,
    AdminPermission.FulfillmentLead,
    AdminPermission.FulfillmentAssociate,
  ])

  const returnedOrdersSidebarVisible = !me.permissions.includes(
    AdminPermission.PartnerProgramFeatures,
  )

  const fbaOrdersSidebarVisible = isAuthorized(me.permissions, [
    AdminPermission.PartnerProgramFeatures,
    AdminPermission.FulfillmentLead,
    AdminPermission.FulfillmentAssociate,
  ])

  const sortingBinSidebarVisible = isAuthorized(me.permissions, [
    AdminPermission.PartnerProgramFeatures,
    AdminPermission.FulfillmentLead,
    AdminPermission.FulfillmentAssociate,
  ])

  const labelTransitionsPageEnabled = useFlag(
    "admin_label_transitions_page_enabled",
  ).isEnabled

  const { data: labelRollAlertCount } = useQuery(
    trpc.labelRoll.lowStockLabelRollsCount.queryOptions(undefined, {
      enabled: labelRollSidebarVisible,
      refetchInterval,
    }),
  )

  const { data: returnedAdminOrdersCount } = useQuery(
    trpc.orders.acceptedReturnedOrdersCount.queryOptions(undefined, {
      enabled: returnedOrdersSidebarVisible,
      refetchInterval,
    }),
  )

  const { data: preparedShelfBinsCount } = useQuery(
    trpc.warehouseShelf.getPreparedShelfBinsCount.queryOptions(
      {},
      {
        enabled: sortingBinSidebarVisible && !!me.warehouseId,
        refetchInterval,
      },
    ),
  )

  const { data: fbaOrdersCount } = useQuery(
    trpc.orders.readyFbaOrdersCount.queryOptions(undefined, {
      enabled: fbaOrdersSidebarVisible,
      refetchInterval,
    }),
  )

  // Defines the sidebar items and their visibility
  const sidebarItemConfigWithVisibility: SideBarItemConfigWithVisibility[] = [
    {
      title: "Dashboard",
      testId: "dashboard",
      icon: PiHouse,
      href: "/",
      isVisible: !me?.permissions.includes(
        AdminPermission.PartnerProgramFeatures,
      ),
    },
    {
      title: "Product Stock",
      testId: "product-stock",
      icon: PiBookOpen,
      href: routes.productsStock,
      isVisible:
        !me?.permissions.includes(AdminPermission.Everything) &&
        isAuthorized(me?.permissions, [AdminPermission.PartnerProgramFeatures]),
    },

    {
      title: "Catalog",
      testId: "catalog",
      icon: PiBookOpen,
      children: [
        {
          title: "Products",
          testId: "products",
          href: routes.products,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.FulfillmentLead,
          ]),
        },
        {
          title: "Extended Catalog",
          testId: "extended-catalog",
          href: routes.extendedProducts,
          isVisible: isAuthorized(me?.permissions, []),
        },
      ],
    },
    {
      title: "User Management",
      testId: "user-management",
      icon: TbUsers,
      children: [
        {
          title: "Users",
          testId: "users",
          href: routes.users,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.LabelVerification,
          ]),
        },
        {
          title: "User Products",
          testId: "user-products",
          href: routes.userProducts,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.FulfillmentLead,
            AdminPermission.LabelVerification,
          ]),
        },
        {
          title: "Label Verification",
          testId: "label-verification",
          href: routes.labelVerification,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.LabelVerification,
          ]),
        },
        {
          title: "Label Transitions",
          testId: "label-transitions",
          href: routes.labelTransitions,
          isVisible: labelTransitionsPageEnabled,
        },
        {
          title: "Post Payments",
          testId: "post-payments",
          href: routes.postPayments,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.Everything,
          ]),
        },
        {
          title: "Amazon FBA Invoices",
          testId: "amazon-fba-invoices",
          href: routes.amazonFbaInvoices,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.Everything,
          ]),
        },
        {
          title: "Bulk Invoices",
          testId: "bulk-invoices",
          href: routes.bulkInvoices,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.Everything,
          ]),
        },
      ],
    },
    {
      title: "Fulfillment",
      testId: "fulfillment",
      icon: TbTruckLoading,
      children: [
        {
          title: "Orders",
          testId: "orders",
          href: routes.orders({ me, warehouses }),
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
        },
        {
          title: "Orders",
          testId: "partner-orders",
          href: routes.partnerOrders,
          isVisible:
            !me?.permissions.includes(AdminPermission.Everything) &&
            isAuthorized(me?.permissions, [
              AdminPermission.PartnerProgramFeatures,
            ]),
        },
        {
          title: "Today's Items",
          testId: "todays-items",
          href: routes.todaysItems({ me, warehouses }),
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
        },
        {
          title: "Amazon FBA",
          testId: "amazon-fba",
          href: routes.amazonFba,
          isVisible: fbaOrdersSidebarVisible,
          alertCount: fbaOrdersCount,
        },
        {
          title: "Batch Printing",
          testId: "batch-printing",
          href: routes.batchPrinting,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.PartnerProgramFeatures,
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
        },
        {
          title: "Labeling",
          testId: "labeling",
          href: routes.labeling(),
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.PartnerProgramFeatures,
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
        },
        {
          title: "Sorting",
          testId: "sorting",
          href: routes.sorting,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.PartnerProgramFeatures,
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
          alertCount: preparedShelfBinsCount,
        },
        {
          title: "Bulk Shipping",
          testId: "bulk-shipping",
          href: routes.bulkShipping,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.PartnerProgramFeatures,
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
        },
        {
          title: "Bulk Shipping Details",
          testId: "bulk-shipping-details",
          href: routes.bulkShippingDetails(),
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.PartnerProgramFeatures,
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
        },
        {
          title: "Label Rolls",
          testId: "label-rolls",
          href: routes.labelRolls({ me, warehouses }),
          isVisible: labelRollSidebarVisible,
          alertCount: labelRollAlertCount,
        },
        {
          title: "Shipment Batches",
          testId: "shipment-batches",
          href: routes.shipmentBatches,
          isVisible:
            isAuthorized(me?.permissions, [
              AdminPermission.PartnerProgramFeatures,
              AdminPermission.FulfillmentLead,
              AdminPermission.FulfillmentAssociate,
            ]) &&
            warehouses
              .flatMap((warehouse) => warehouse.carriers)
              .some((carrier) => carrier?.manifestAvailable),
        },
        {
          title: "Completed Shipments",
          testId: "completed-shipments",
          href: routes.completedShipments,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.PartnerProgramFeatures,
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
        },
        {
          title: "Returned Orders",
          testId: "returned-orders",
          href: routes.returnedOrders,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.FulfillmentLead,
            AdminPermission.FulfillmentAssociate,
          ]),
          alertCount: returnedAdminOrdersCount,
        },
      ],
    },
    {
      title: "Fulfillment Settings",
      testId: "fulfillment-settings",
      icon: TbAdjustments,
      children: [
        {
          title: "Packaging",
          testId: "packaging",
          href: routes.packaging,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.FulfillmentLead,
            AdminPermission.PartnerProgramFeatures,
          ]),
        },
        {
          title: "Warehouse Shelves",
          testId: "warehouse-shelves",
          href: routes.warehouseShelves,
          isVisible: isAuthorized(me?.permissions, [
            AdminPermission.FulfillmentLead,
            AdminPermission.PartnerProgramFeatures,
          ]),
        },
        {
          title: "Carriers",
          testId: "carriers",
          href: routes.carriers,
          isVisible: isAuthorized(me?.permissions, []),
        },
        {
          title: "Warehouses",
          testId: "warehouses",
          href: routes.warehouses,
          isVisible: isAuthorized(me?.permissions, []),
        },
      ],
    },
    {
      title: "Site Settings",
      testId: "site-settings",
      icon: RiPagesLine,
      children: [
        {
          title: "System Notifications",
          testId: "system-notifications",
          href: routes.systemNotifications,
          isVisible: isAuthorized(me?.permissions, []),
        },
      ],
    },
  ]

  // Filter out items that are not visible
  const sidebarItemConfig: SidebarItemConfig[] = sidebarItemConfigWithVisibility
    .filter((item) => {
      if (item.children) {
        const children = item.children.filter((child) => child.isVisible)
        return children.length > 0
      }

      return item.isVisible
    })
    .map((item) => {
      if (item.children) {
        return {
          ...item,
          children: item.children.filter((child) => child.isVisible),
        }
      }

      return item
    })

  return {
    sidebarItemConfig,
  }
}
